import { useEffect, useMemo, useState } from 'react';

import type { Opportunity } from '../../shared/opportunity';
import { traveltime, type TravelTimeClient } from './traveltime';
import { eventPoints, makeTravelTimes, type Shed, type TravelTimes } from './travelTimes';
import { walkshed, type LatLng, type TravelCity, type TravelMode } from './walkshed';

/**
 * TravelTime, wired to React (contract §17).
 *
 * One time-filter per origin and mode, whatever the slider says: the slider
 * only moves a threshold over minutes already known, so dragging it filters
 * the list at once. One time-map per origin, mode and minutes, asked for 300 ms
 * after the slider settles; until it lands the previous polygon stays drawn
 * (never a blank map), or, from a new origin, the geometric estimate, dashed. Cached answers are read during render, so a reload or a
 * slide back to a step already seen draws immediately.
 */

/** How long the slider has to rest before a new isochrone is asked for. */
export const SLIDER_SETTLE_MS = 300;

export interface TravelData {
  /** Null without an origin or before the city file has loaded. */
  times: TravelTimes | null;
  /** What the map draws; null with no limit or no origin. */
  shed: Shed | null;
  /** Still waiting on TravelTime for the minutes (the list shows estimates meanwhile). */
  loading: boolean;
}

interface Inputs {
  origin: LatLng | null;
  mode: TravelMode;
  withinMinutes: number | null;
  city: TravelCity | null;
  /** Every listing the filters run over; their points are what time-filter measures to. */
  events: Opportunity[];
  client?: TravelTimeClient;
}

type Answer<T> = { key: string; value: T } | { key: string; failed: true };

const geometricShed = (origin: LatLng, mode: TravelMode, minutes: number, city: TravelCity): Shed => ({
  geometry: walkshed(origin, mode, minutes, city),
  minutes,
  approximate: true,
});

export function useTravelTimes({ origin, mode, withinMinutes, city, events, client = traveltime }: Inputs): TravelData {
  const points = useMemo(() => (city ? eventPoints(events, city) : []), [events, city]);
  const originKey = origin ? `${origin[0]},${origin[1]}` : '';
  const pointsKey = useMemo(() => points.map((p) => p.id).join(';'), [points]);

  // --- time-filter: the minutes --------------------------------------------------------
  // Nothing to ask about until the city file has loaded.
  const filterKey = origin && client.available && points.length ? `${originKey}|${mode}|${pointsKey}` : '';
  const [filterAnswer, setFilterAnswer] = useState<Answer<ReadonlyMap<string, number | null>> | null>(null);
  const peekedFilter = useMemo(
    () => (filterKey && origin ? client.peekTimeFilter(origin, mode, points) : undefined),
    // The key names everything the peek reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterKey],
  );
  useEffect(() => {
    if (!filterKey || !origin || peekedFilter) return;
    let live = true;
    client.timeFilter(origin, mode, points).then(
      (value) => live && setFilterAnswer({ key: filterKey, value }),
      () => live && setFilterAnswer({ key: filterKey, failed: true }),
    );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, peekedFilter]);
  const filterNow = filterAnswer?.key === filterKey ? filterAnswer : null;
  const filtered = peekedFilter ?? (filterNow && 'value' in filterNow ? filterNow.value : null);
  const filterFailed = Boolean(filterNow && 'failed' in filterNow);

  // --- time-map: the polygon -------------------------------------------------------------
  const mapKey = origin && withinMinutes != null && client.available ? `${originKey}|${mode}|${withinMinutes}` : '';
  const [mapAnswer, setMapAnswer] = useState<Answer<Shed> | null>(null);
  /**
   * The last isochrone that landed from this origin, drawn while the next one
   * loads. Not across origins: a shape around the old place would pull the
   * map's fit back to it.
   */
  const [lastShed, setLastShed] = useState<{ originKey: string; shed: Shed } | null>(null);
  const peekedMap = useMemo(() => {
    if (!mapKey || !origin || withinMinutes == null) return undefined;
    const geometry = client.peekTimeMap(origin, mode, withinMinutes);
    return geometry ? { geometry, minutes: withinMinutes, approximate: false } : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapKey]);
  useEffect(() => {
    if (!mapKey || !origin || withinMinutes == null || peekedMap) return;
    let live = true;
    const minutes = withinMinutes;
    const timer = window.setTimeout(() => {
      client.timeMap(origin, mode, minutes).then(
        (geometry) => {
          if (!live) return;
          const shed = { geometry, minutes, approximate: false };
          setMapAnswer({ key: mapKey, value: shed });
        },
        () => live && setMapAnswer({ key: mapKey, failed: true }),
      );
    }, SLIDER_SETTLE_MS);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapKey, peekedMap]);
  const mapNow = mapAnswer?.key === mapKey ? mapAnswer : null;
  const landed = peekedMap ?? (mapNow && 'value' in mapNow ? mapNow.value : null);
  const mapFailed = !client.available || Boolean(mapNow && 'failed' in mapNow);

  // Remember what landed, for the next wait. Set during render, React's way of
  // deriving state from a change, so the held polygon is never a frame late.
  if (landed && lastShed?.shed !== landed) setLastShed({ originKey, shed: landed });
  const held = lastShed?.originKey === originKey ? lastShed.shed : null;

  const shed = useMemo<Shed | null>(() => {
    if (!origin || withinMinutes == null || !city) return null;
    if (landed) return landed;
    if (mapFailed) return geometricShed(origin, mode, withinMinutes, city);
    // Waiting: the last real polygon from here if there is one, else the estimate.
    return held ?? geometricShed(origin, mode, withinMinutes, city);
  }, [origin, withinMinutes, city, landed, mapFailed, held, mode]);

  const times = useMemo(() => {
    if (!origin || !city) return null;
    // Only the isochrone for this very origin, mode and limit can stand in for the minutes.
    return makeTravelTimes({ origin, mode, city, filtered, shed: filtered ? null : landed });
  }, [origin, city, mode, filtered, landed]);

  const loading = Boolean(filterKey) && !filtered && !filterFailed;
  return { times, shed, loading };
}
